import React, { useState, useEffect, useRef } from 'react';
import { useEnvironment } from '@/hooks/useEnvironment';
import { getReportIssueForm, submitIssue } from '@/services/authService';
import { useToast } from '@/components/ui/use-toast';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { AlertTriangle, FileEdit, CheckCircle } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { useApiState } from '@/hooks/useApiState';
import { useLanguage } from '@/hooks/useLanguage';

type ReportIssueQuestion = {
  qid: number;
  q: string;
  qcode: string;
  inty: 'select' | 'text';
  opts: string; // stringified array
};
type ReportIssueForm = {
  qs: ReportIssueQuestion[];
  bs: { hts: string }; // stringified array
};

// Helper to map API status/message to user-friendly message for report issue
function mapReportIssueError(status, message, t) {
  if (status === 504) return t('error', 'reportIssue');
  if (status === 503) return t('error', 'reportIssue');
  if (status === 409) return t('error', 'reportIssue');
  if (status === 500) return t('error', 'reportIssue');
  if (status === 400) return t('error', 'reportIssue');
  if (status === 401) return t('error', 'reportIssue');
  if (status === 404) return t('error', 'reportIssue');
  if (message && message.toLowerCase().includes('successfully submitted')) return t('success', 'reportIssue');
  return message || t('error', 'reportIssue');
}

const ReportIssueSection = () => {
  const { isNativeWebView } = useEnvironment();
  const [form, setForm] = useState<ReportIssueForm | null>(null);
  const { loading, setLoading, error, setError } = useApiState();
  const [submitLoading, setSubmitLoading] = useState(false);
  const [responseMessage, setResponseMessage] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const { toast } = useToast();
  const { t, tArray } = useLanguage();

  // Refs for uncontrolled inputs
  const inputRefs = useRef<Record<number, HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement | null>>({});

  useEffect(() => {
    setLoading(true);
    getReportIssueForm()
      .then((data: ReportIssueForm) => {
        setForm(data);
        setLoading(false);
      })
      .catch((err: Error) => {
        setError(err.message || 'Failed to load form');
        setLoading(false);
      });
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form?.qs) return;
    
    setSubmitLoading(true);
    setResponseMessage(null);
    try {
      // Build the new answer array for the API
      const answers = form.qs.map((q) => {
        const ref = inputRefs.current[q.qid];
        return {
          qid: q.qid,
          qcode: q.qcode,
          ans: String(ref ? ref.value : ''),
        };
      });
      const res = await submitIssue({ ans: answers });
      const userMessage = mapReportIssueError(res.status, res.message, t);
      
      if (isNativeWebView) {
        setResponseMessage({
          type: res.ok ? 'success' : 'error',
          message: userMessage
        });
      } else {
        toast({
          title: res.ok ? 'Success' : 'Error',
          description: userMessage,
          variant: res.ok ? 'success' : 'destructive',
          duration: 3000,
        });
      }
      
      if (res.ok && form?.qs) {
        // Reset the input values after successful submit
        form.qs.forEach((q) => {
          const ref = inputRefs.current[q.qid];
          if (ref) ref.value = '';
        });
      }
    } catch (err: any) {
      const errorMsg = 'Something went wrong. Please try again later.';
      if (isNativeWebView) {
        setResponseMessage({
          type: 'error',
          message: errorMsg
        });
      } else {
        toast({
          title: 'Error',
          description: errorMsg,
          variant: 'destructive',
          duration: 3000,
        });
      }
    } finally {
      setSubmitLoading(false);
    }
  };

  // Web UI
  const WebDesign = () => (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-white py-12 px-2">
      <div className="max-w-2xl mx-auto">
        <div className="flex flex-col items-center mb-10">
          <div className="flex items-center gap-2 mb-2">
            <FileEdit className="w-8 h-8 text-orange-400" />
            <h1 className="text-3xl font-bold text-gray-900 tracking-tight">{t('title', 'reportIssue')}</h1>
          </div>
          <p className="text-gray-600 text-lg text-center max-w-xl">
            {t('subtitle', 'reportIssue')}
          </p>
        </div>
        <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-lg p-8">
          {form?.qs?.map((item, idx) => {
            let opts: string[] = [];
            if (item.inty === 'select') {
              try {
                opts = JSON.parse(item.opts);
              } catch {
                opts = [];
              }
            }
            return (
              <div key={item.qid || item.qcode || idx} className="mb-6">
                <label className="block text-base font-semibold mb-2 text-gray-800">
                  {item.q} <span className="text-red-500">*</span>
                </label>
                {item.inty === 'select' ? (
                  <select
                    required
                    className="w-full h-12 px-4 border border-gray-200 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-orange-300 transition"
                    ref={el => (inputRefs.current[item.qid] = el)}
                    defaultValue={''}
                  >
                    <option value="" disabled>
                      {t('selectOption', 'reportIssue')}
                    </option>
                    {opts.map((opt: string, optIdx: number) => (
                      <option key={optIdx} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    required
                    type="text"
                    className="w-full h-12 px-4 border border-gray-200 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-orange-300 transition"
                    placeholder={item.q}
                    ref={el => (inputRefs.current[item.qid] = el)}
                  />
                )}
              </div>
            );
          })}
          {/* Info box before submit */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4 text-yellow-900 text-base mb-4 flex items-start gap-3">
            <AlertTriangle className="w-6 h-6 text-yellow-500 mt-1" />
            <div>
              <div className="font-semibold mb-1">{t('beforeSubmit', 'reportIssue')}</div>
              <ul className="list-disc pl-5 space-y-1">
                {(function() { let hints: string[] = []; try { hints = form?.bs?.hts ? JSON.parse(form.bs.hts) : []; } catch { hints = []; } return hints; })().map((point, index) => (
                  <li key={index}>{point}</li>
                ))}
              </ul>
            </div>
          </div>
          <Button
            type="submit"
            className="mt-4 w-full h-12 text-base bg-orange-500 hover:bg-orange-600 transition"
            disabled={submitLoading}
          >
            {submitLoading ? t('submitting', 'reportIssue') : t('submitIssue', 'reportIssue')}
          </Button>
        </form>
      </div>
    </div>
  );

  // Mobile UI: iPhone-style, modern, clean, edge-to-edge
  const WebViewDesign = () => (
    <div className="bg-gray-50 min-h-screen pb-4">
      <div className="sticky top-0 z-10 bg-gray-50 flex justify-between items-center px-4 py-3 border-b border-gray-200">
        <div className="font-semibold text-lg text-gray-900">{t('title', 'reportIssue')}</div>
      </div>
      {responseMessage && (
        <div className={`mx-4 mt-2 p-3 rounded-xl border ${responseMessage.type === 'success' ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-800'}`}>
          <div className="flex items-center gap-2">
            {responseMessage.type === 'success' ? (
              <CheckCircle className="w-5 h-5 text-green-600" />
            ) : (
              <AlertTriangle className="w-5 h-5 text-red-600" />
            )}
            <span className="text-sm font-medium">{responseMessage.message}</span>
          </div>
        </div>
      )}
      <form onSubmit={handleSubmit} className="space-y-6 px-4 pt-2">
        <section className="bg-white rounded-2xl shadow-sm p-4">
          {form?.qs?.map((item, idx) => {
            let opts: string[] = [];
            if (item.inty === 'select') {
              try {
                opts = JSON.parse(item.opts);
              } catch {
                opts = [];
              }
            }
            return (
              <div key={item.qid || item.qcode || idx} className="mb-5">
                <label className="block text-sm font-medium text-gray-600 mb-2">
                  {item.q} <span className="text-red-500">*</span>
                </label>
                {item.inty === 'select' ? (
                  <select
                    required
                    className="w-full h-12 px-4 border border-gray-200 rounded-xl text-base focus:outline-none focus:ring-2 focus:ring-blue-300 transition bg-gray-50"
                    ref={el => (inputRefs.current[item.qid] = el)}
                    defaultValue={''}
                  >
                    <option value="" disabled>{t('selectOption', 'reportIssue')}</option>
                    {opts.map((opt: string, optIdx: number) => (
                      <option key={optIdx} value={opt}>{opt}</option>
                    ))}
                  </select>
                ) : (
                  <input
                    required
                    type="text"
                    className="w-full h-12 px-4 border border-gray-200 rounded-xl text-base focus:outline-none focus:ring-2 focus:ring-blue-300 transition bg-gray-50"
                    placeholder={item.q}
                    ref={el => (inputRefs.current[item.qid] = el)}
                  />
                )}
              </div>
            );
          })}
        </section>
        {(function() { let hints: string[] = []; try { hints = form?.bs?.hts ? JSON.parse(form.bs.hts) : []; } catch { hints = []; } return hints; })().length > 0 && (
          <section className="bg-blue-50 rounded-2xl shadow-sm p-4 mt-2">
            <div className="font-semibold text-blue-700 mb-2 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-blue-500" />
              {t('beforeSubmit', 'reportIssue')}
            </div>
            <ul className="list-disc pl-5 space-y-1 text-blue-900 text-sm">
              {(function() { let hints: string[] = []; try { hints = form?.bs?.hts ? JSON.parse(form.bs.hts) : []; } catch { hints = []; } return hints; })().map((hint: string, hintIdx: number) => (
                <li key={hintIdx}>{hint}</li>
              ))}
            </ul>
          </section>
        )}
        <div className="fixed bottom-0 left-0 right-0 px-4 pb-4 bg-gradient-to-t from-gray-50 via-gray-50/80 to-transparent z-20">
          <Button
            type="submit"
            className="w-full h-12 text-base rounded-xl text-white font-semibold shadow-md"
            style={{ backgroundColor: '#400F46' }}
            disabled={submitLoading}
          >
            {submitLoading ? t('submitting', 'reportIssue') : t('submitIssue', 'reportIssue')}
          </Button>
        </div>
      </form>
    </div>
  );

  // Mobile UI: Stacked, touch-friendly layout
  const WebViewSkeleton = () => (
    <Card className="mx-2">
      <CardHeader />
      <CardContent className="space-y-4">
        {[1,2,3,4].map(i => (
          <div key={i}>
            <Skeleton className="h-4 w-1/2 mb-2" />
            <Skeleton className="h-12 w-full mb-2" />
          </div>
        ))}
        <Skeleton className="h-20 w-full mb-2" />
        <Skeleton className="h-10 w-1/2 mb-2" />
        <Skeleton className="h-12 w-full" />
      </CardContent>
    </Card>
  );

  if (loading) {
    if (isNativeWebView) return <WebViewSkeleton />;
    return <div className="p-8 text-center text-gray-600 font-serif">{t('loading', 'common')}</div>;
  }
  if (error) {
    toast({
      title: 'Error',
      description: error,
      variant: 'destructive',
      duration: 5000,
    });
  }
  if (!form) return null;
  return isNativeWebView ? <WebViewDesign /> : <WebDesign />;
};

export { ReportIssueSection };